import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import normalizeHelpSections from "./normalizeHelpSections";
import HelpGuideSectionAccordion from "./HelpGuideSectionAccordion";

const MOBILE_MEDIA_QUERY = "(max-width: 639px)";

const textMatches = (value, query) =>
  String(value || "").toLowerCase().includes(query.toLowerCase());

const bulletToText = (bullet) =>
  typeof bullet === "string" ? bullet : String(bullet?.text || "").trim();

const sectionMatchesQuery = (section, query) => {
  if (!query) return true;
  if (textMatches(section.title, query) || textMatches(section.description, query)) {
    return true;
  }
  if (Array.isArray(section.items)) {
    const itemMatch = section.items.some((item) => {
      if (textMatches(item.label, query) || textMatches(item.body, query)) {
        return true;
      }
      if (Array.isArray(item.details) && item.details.some((detail) => textMatches(detail, query))) {
        return true;
      }
      return false;
    });
    if (itemMatch) return true;
  }
  if (
    Array.isArray(section.bullets) &&
    section.bullets.some((item) => textMatches(bulletToText(item), query))
  ) {
    return true;
  }
  if (Array.isArray(section.tips) && section.tips.some((item) => textMatches(item, query))) {
    return true;
  }
  return false;
};

const buildDefaultOpenMap = (sections, isMobile) => {
  const next = {};
  if (!Array.isArray(sections) || sections.length === 0) return next;
  sections.forEach((section, idx) => {
    next[section.id] = isMobile ? idx === 0 : true;
  });
  return next;
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mediaQueryList = window.matchMedia(MOBILE_MEDIA_QUERY);
    const listener = (event) => setIsMobile(event.matches);
    setIsMobile(mediaQueryList.matches);
    mediaQueryList.addEventListener("change", listener);
    return () => mediaQueryList.removeEventListener("change", listener);
  }, []);

  return isMobile;
};

export default function HelpGuidePanel({
  title,
  intro,
  sections,
  troubleshooting,
  onClose,
  onJumpToSection,
  layout = "drawer",
  footer = null,
  dialogRef,
  closeButtonRef,
}) {
  const isMobile = useIsMobile();
  const isModal = layout === "modal";
  const [searchQuery, setSearchQuery] = useState("");
  const [contentsOpen, setContentsOpen] = useState(() => !isMobile);
  const [openSections, setOpenSections] = useState({});
  const sectionRefs = useRef({});

  const normalizedSections = useMemo(() => normalizeHelpSections(sections), [sections]);
  const filteredSections = useMemo(
    () => normalizedSections.filter((section) => sectionMatchesQuery(section, searchQuery)),
    [normalizedSections, searchQuery]
  );

  useEffect(() => {
    setSearchQuery("");
    setContentsOpen(!isMobile);
    setOpenSections(buildDefaultOpenMap(normalizedSections, isMobile));
  }, [normalizedSections, isMobile]);

  useEffect(() => {
    if (!isMobile || filteredSections.length === 0) return;
    const hasVisibleOpenSection = filteredSections.some((section) => openSections[section.id]);
    if (hasVisibleOpenSection) return;
    setOpenSections((prev) => ({
      ...prev,
      [filteredSections[0].id]: true,
    }));
  }, [filteredSections, isMobile, openSections]);

  const toggleSection = (sectionId) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const scrollToGuideSection = (sectionId) => {
    const target = sectionRefs.current[sectionId];
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleJump = (section) => {
    if (!section) return;

    if (section.anchorId && typeof onJumpToSection === "function") {
      onJumpToSection(section.anchorId);
      if (isMobile) {
        window.setTimeout(() => {
          onClose?.();
        }, 200);
      }
      return;
    }

    scrollToGuideSection(section.id);
  };

  const containerClass = isModal
    ? "fixed inset-0 z-[10060] flex items-stretch justify-center sm:items-center"
    : "fixed inset-0 z-[10060] flex flex-col sm:items-stretch sm:justify-end";
  const panelClass = isModal
    ? "relative flex h-full w-full flex-col overflow-hidden rounded-none bg-white shadow-2xl sm:h-[80vh] sm:max-h-[80vh] sm:max-w-[760px] sm:rounded-3xl md:max-w-[840px] lg:max-w-[900px]"
    : "relative mt-auto flex h-[92vh] w-full flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:mt-0 sm:ml-auto sm:h-full sm:max-w-xl sm:rounded-l-3xl sm:rounded-tr-3xl lg:max-w-lg";
  const headerClass = isModal
    ? "sticky top-0 z-10 flex items-center justify-between gap-3 bg-rose-500 px-5 py-4 sm:rounded-t-3xl"
    : "sticky top-0 z-10 flex items-center justify-between gap-3 rounded-t-3xl bg-rose-500 px-5 py-4 sm:rounded-tl-3xl sm:rounded-tr-3xl";
  const bodyClass = isModal
    ? "flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5"
    : "flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5";

  return (
    <div className={containerClass}>
      <button
        type="button"
        aria-label="Close help guide overlay"
        className="fixed inset-0 bg-slate-900/40"
        onClick={onClose}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={panelClass}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={headerClass}>
          <h2 className="text-lg font-semibold text-white md:text-xl">{title}</h2>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="h-9 w-9 rounded border border-white/40 bg-white/10 text-sm font-semibold text-white transition hover:bg-white/20"
          >
            X
          </button>
        </div>

        <div className={bodyClass}>
          {intro ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              {intro}
            </div>
          ) : null}

          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <label htmlFor="help-guide-search" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Search
            </label>
            <input
              id="help-guide-search"
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search this guide…"
              className="mt-2 h-10 w-full rounded-xl border border-slate-300 px-3 text-sm text-slate-700 outline-none ring-rose-300 transition focus:border-rose-300 focus:ring-2"
            />
          </div>

          <section className="rounded-2xl border border-slate-200 bg-white p-4">
            <button
              type="button"
              className="flex w-full items-center justify-between gap-3 text-left"
              aria-expanded={contentsOpen}
              onClick={() => setContentsOpen((prev) => !prev)}
            >
              <span className="text-base font-semibold text-slate-900">Contents</span>
              <span className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600">
                {contentsOpen ? "Hide" : "Show"}
              </span>
            </button>
            {contentsOpen ? (
              filteredSections.length > 0 ? (
                <ul className="mt-3 space-y-2">
                  {filteredSections.map((section) => (
                    <li key={`contents-${section.id}`}>
                      <button
                        type="button"
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                        onClick={() => handleJump(section)}
                      >
                        {section.title}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-sm text-slate-500">No matching sections.</p>
              )
            ) : null}
          </section>

          <div className="space-y-4">
            {filteredSections.map((section) => (
              <HelpGuideSectionAccordion
                key={section.id}
                section={section}
                isOpen={Boolean(openSections[section.id])}
                onToggle={() => toggleSection(section.id)}
                showToggle
                onGoToSection={handleJump}
                sectionRef={(node) => {
                  sectionRefs.current[section.id] = node;
                }}
                isMobile={isMobile}
              />
            ))}
            {filteredSections.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-5 text-sm text-slate-500">
                No sections match your search.
              </div>
            ) : null}
          </div>

          {Array.isArray(troubleshooting) && troubleshooting.length > 0 ? (
            <section className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
              <h3 className="text-base font-semibold text-slate-900">Troubleshooting</h3>
              <div className="space-y-2">
                {troubleshooting.map((item) => (
                  <div
                    key={item.title}
                    className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2"
                  >
                    <p className="text-sm font-semibold text-slate-900">{item.title}</p>
                    <p className="text-sm text-slate-600">{item.body}</p>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </div>
        {footer ? (
          <div className="border-t border-slate-200 bg-white px-4 py-3 sm:px-5 sm:py-4 flex-shrink-0">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

HelpGuidePanel.propTypes = {
  title: PropTypes.string,
  intro: PropTypes.string,
  sections: PropTypes.arrayOf(PropTypes.object),
  troubleshooting: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      body: PropTypes.string,
    })
  ),
  onClose: PropTypes.func,
  onJumpToSection: PropTypes.func,
  layout: PropTypes.oneOf(["drawer", "modal"]),
  footer: PropTypes.node,
  dialogRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  closeButtonRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
};
